import { useMemo, useState } from "react";
import { useLocale } from "@hooks/useLocale";
import { useCurrentWell } from "@hooks/useCurrentWell";
import { QueryState } from "@components/ui/QueryState";
import { useGetEventReportsQuery } from "@api/pApi";
import type { DailyReportOut } from "@api/types";
import { MudPanel } from "./MudPanel";

/**
 * Mud / Fluids screen (§8) — the standalone route. Resolves the current well's
 * active event, lets the user pick a daily report (mud is report-scoped), and
 * renders the day's properties/products/volumes plus the event-wide property
 * trend. Mirrors the DailyReport report-selector so the well switcher drives it.
 */
export function Mud() {
  const { t } = useLocale();
  const { well, activeEvent, isLoading: ctxLoading, isError: ctxError } = useCurrentWell();

  const eventId = activeEvent?.id ?? "";
  const {
    data: reports = [],
    isLoading,
    isError,
    refetch,
  } = useGetEventReportsQuery(eventId, { skip: !eventId });

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = useMemo<DailyReportOut | undefined>(() => {
    if (selectedId) return reports.find((r) => r.id === selectedId) ?? reports[0];
    return reports[0];
  }, [reports, selectedId]);

  return (
    <div>
      <div className="page-head">
        <h1>{t("mud.title")}</h1>
        <p className="subtitle">
          {well ? `${well.commonName} · ${well.field}` : t("well.notSelected")}
        </p>
      </div>

      <QueryState
        isLoading={ctxLoading || isLoading}
        isError={ctxError || isError}
        isEmpty={!activeEvent || reports.length === 0}
        emptyMessage={!activeEvent ? t("well.event.noEvents") : t("mud.noData")}
        onRetry={refetch}
      >
        {selected ? (
          <>
            <div className="ddr-toolbar">
              <div className="field">
                <label htmlFor="mud-report">{t("mud.selectReport")}</label>
                <select
                  id="mud-report"
                  value={selected.id}
                  onChange={(e) => setSelectedId(e.target.value)}
                >
                  {reports.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.date_report} · {t(r.state === "closed" ? "state.closed" : "state.open")}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <MudPanel
              reportId={selected.id}
              eventId={eventId}
              reportDate={selected.date_report}
              unitProfileKey={selected.unit_profile_key}
              currency={selected.currency}
              readOnly={selected.state === "closed"}
            />
          </>
        ) : null}
      </QueryState>
    </div>
  );
}
